import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DescriptionOutlined, ErrorOutlineOutlined, InfoOutlined, ListAltOutlined, PriorityHighRounded, ReplayRounded, WarningAmberRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { Loader } from "components/Loader";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { ptValidationHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { RecordHeaderCard, StatCardGrid, StatutoryList, ToolbarAddButton, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { SEVERITY_COLORS, fetchPtIssue, fetchPtValidation, recheckPtValidation } from "./ptReportsApi";

// Validation (PT6) from GET validation/ and the Validation Issue drawer
// (PT6a) from GET validation/issues/<id>/. Checks run by themselves (payroll
// finalised, version / exemption saved, payment recorded, due date passed)
// and with Re-check now (POST validation/recheck/). PT issues never stop
// payroll; only a missing date of birth holds that one employee. Filters by
// month and site — the Department filter does not apply here. The list is
// not paged, so search and column filters work on the loaded issues.

const ALL = "all";
const { pink, blue, green } = detailAccentPalette;
const SEVERITY_TONES = { BLOCKING: "danger", WARNING: "warning", INFO: "teal" };

// fix_target -> where the drawer's main button goes.
const CONFIG_ROUTE = "/admin/payroll/configuration/statutory-configuration?item=pt";
const EMPLOYEES_ROUTE = "/admin/payroll/statutory/pt";
const EMPLOYEE_PROFILE_ROUTE = "/admin/hrzone/employees";
const PAYROLL_ROUTE = "/admin/payroll/pay-processing/payroll-cycle-run";

const IssuePanel = ({ open, domain, companyId, issueId, onClose, onRecheck, rechecking, goTo }) => {
  const history = useHistory();
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    if (!open || issueId == null) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: "", data: null });
    fetchPtIssue({ domain, companyId, issueId, signal: controller.signal })
      .then((data) => setState({ loading: false, error: "", data }))
      .catch((error) => {
        if (!controller.signal.aborted) setState({ loading: false, error: error.message || "Could not load the issue.", data: null });
      });
    return () => controller.abort();
  }, [open, domain, companyId, issueId]);

  const issue = state.data;
  const openFix = (action) => {
    const target = action.fix_target || issue.fix_target;
    const { site_id: siteId, period_id: periodId } = issue.links;
    onClose();
    if (target === "PT_CONFIGURATION") history.push(siteId ? `${CONFIG_ROUTE}&site=${siteId}` : CONFIG_ROUTE);
    else if (target === "PT_EMPLOYEES") history.push(EMPLOYEES_ROUTE);
    else if (target === "EMPLOYEE_PROFILE") history.push(EMPLOYEE_PROFILE_ROUTE);
    else if (target === "PAYROLL") history.push(PAYROLL_ROUTE);
    else if (target === "PAYMENTS") goTo("payments", periodId ? { periodId, kind: "view" } : {});
  };

  const fix = issue?.actions.find((action) => action.action === "OPEN_FIX");
  const recalc = issue?.actions.find((action) => action.action === "RECALCULATE");
  const recheck = issue?.actions.find((action) => action.action === "RECHECK" && action.enabled);

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Validation Issue"
      subtitle={issue ? [issue.severity_label, issue.site, issue.month_label].filter(Boolean).join(" · ") : ""}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {recheck ? (
            <SecondaryButton startIcon={<ReplayRounded />} onClick={onRecheck} disabled={rechecking}>
              {rechecking ? "Checking…" : "Re-check now"}
            </SecondaryButton>
          ) : null}
          {recalc ? (
            <Mui.Tooltip title={recalc.enabled ? "" : recalc.note || "Payroll already finalised"} arrow>
              <span>
                <SecondaryButton
                  disabled={!recalc.enabled}
                  onClick={() => {
                    onClose();
                    history.push(PAYROLL_ROUTE);
                  }}
                >
                  Calculate again
                </SecondaryButton>
              </span>
            </Mui.Tooltip>
          ) : null}
          {fix ? <PrimaryButton onClick={() => openFix(fix)}>{fix.fix_target === "PT_CONFIGURATION" && issue.rule_code === "SITE_NOT_SET_UP" ? "Set up site" : fix.label || "Open"}</PrimaryButton> : null}
        </Mui.Stack>
      }
    >
      {state.loading ? (
        <Loader info="Loading..." minHeight="40vh" />
      ) : state.error ? (
        <RequestInfoBanner tone="danger" title="Couldn't load the issue." body={state.error} />
      ) : issue ? (
        <Mui.Stack spacing={2}>
          <RecordHeaderCard
            avatar={initialsOf(issue.title)}
            title={issue.title}
            chip={<RequestStatusChip status={issue.severity_label} colorCode={SEVERITY_COLORS[issue.severity]} />}
            meta={[issue.emp_id, issue.site, issue.month_label].filter(Boolean).join(" · ")}
            tone={SEVERITY_TONES[issue.severity] || "teal"}
          />
          {issue.holds_employee_payroll ? <RequestInfoBanner tone="warning" body="This employee's payroll is held until it is fixed. Other employees finalise." /> : null}
          <RequestDetailsSection
            title="ISSUE"
            fields={[
              { icon: PriorityHighRounded, label: "Problem", value: issue.problem || "—", accent: pink, multiline: true },
              ...(issue.effect ? [{ icon: DescriptionOutlined, label: "Effect", value: issue.effect, accent: blue, multiline: true }] : []),
              ...(issue.impact && issue.impact !== issue.effect ? [{ icon: InfoOutlined, label: "Impact", value: issue.impact, accent: blue, multiline: true }] : []),
            ]}
          />
          {issue.steps.length ? (
            <RequestDetailsSection
              title="HOW TO FIX"
              fields={issue.steps.map((step) => ({
                icon: () => (
                  <Mui.Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                    {step.step}
                  </Mui.Box>
                ),
                label: step.title,
                value: step.detail || "—",
                accent: green,
                multiline: true,
              }))}
            />
          ) : (
            <RequestInfoBanner body="Nothing to fix — this is for your information." />
          )}
        </Mui.Stack>
      ) : null}
    </GlobalSidePanel>
  );
};

export const PtValidationTab = ({ domain, companyId, month, siteId, goTo, onChanged }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", issues: [], cards: {}, last_run_label: "", month: "", month_label: "" });
  const [viewId, setViewId] = useState(null);
  const [rechecking, setRechecking] = useState(false);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pt_validation_saved_views",
    fullHeaders: ptValidationHeader,
  });

  const severity = card === ALL ? undefined : card;
  useEffect(() => {
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchPtValidation({ domain, companyId, month, siteId, severity, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (!controller.signal.aborted) setList((current) => ({ ...current, loading: false, error: error.message || "Could not load PT validation." }));
      });
    return () => controller.abort();
  }, [domain, companyId, month, siteId, severity, reloadKey]);

  const recheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    try {
      // Without a finalised month the server checks the current month; re-check that one.
      const result = await recheckPtValidation({ domain, companyId, month: month || list.month });
      showToast("success", result.message || "Checked again.");
      setReloadKey((key) => key + 1);
      onChanged();
    } catch (error) {
      showToast("error", error.message || "Couldn't run the checks.");
    } finally {
      setRechecking(false);
    }
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return row.is_employee ? (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>{initialsOf(row.name)}</Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        ) : (
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
        );
      case "severity":
        return <RequestStatusChip status={row.severity_label} colorCode={SEVERITY_COLORS[row.severity_code]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: ALL, title: "All issues", value: list.cards.all ?? 0, icon: ListAltOutlined, tone: "blue" },
          { key: "BLOCKING", title: "Blocking", value: list.cards.blocking ?? 0, icon: ErrorOutlineOutlined, tone: "red" },
          { key: "WARNING", title: "Warning", value: list.cards.warning ?? 0, icon: WarningAmberRounded, tone: "amber" },
          { key: "INFO", title: "Info", value: list.cards.info ?? 0, icon: InfoOutlined, tone: "green" },
        ]}
      />
      <Mui.Typography sx={{ ...designType.captionText, mb: 1 }}>{list.last_run_label || "Not checked yet for this month."}</Mui.Typography>
      {list.error ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load PT validation."
            body={list.error}
            action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        </Mui.Box>
      ) : null}
      <StatutoryList
        title={`PT Validation ${list.month_label}`.trim()}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.issues.map((issue) => ({ ...issue, severity: issue.severity_label, severity_code: issue.severity }))}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="issues"
        searchPlaceholder="Search name, ID, site or issue"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        action={
          <ToolbarAddButton startIcon={<ReplayRounded />} onClick={recheck} disabled={rechecking}>
            {rechecking ? "Checking…" : "Re-check now"}
          </ToolbarAddButton>
        }
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Checks run by themselves when payroll is finalised, a version or exemption is saved, a payment is recorded, and the day after a payment due date. Use Re-check now after fixing something. Blocking means PT could not be worked out for those employees — the rest of payroll still finalises." />
      </Mui.Box>
      <IssuePanel
        open={viewId != null}
        domain={domain}
        companyId={companyId}
        issueId={viewId}
        onClose={() => setViewId(null)}
        onRecheck={async () => {
          await recheck();
          setViewId(null);
        }}
        rechecking={rechecking}
        goTo={goTo}
      />
    </>
  );
};
